import React from 'react'

import { useTranslation } from 'react-i18next'

import { TermosDaProposta } from '@/features/proposta/components/termos-da-proposta'
import type { ParticipacaoResumo } from '@/features/proposta/entities/ResumoProposta/resumo-participacoes-imobiliarias'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import {
  Button,
  SectionContent,
  SectionGroup,
  SectionHeader,
  SectionItem,
  SectionItemValue,
  SectionTitle,
} from '@/shared/components/ui'

type TDetalhesPropostaSectionProps = {
  resumoParticipacao: readonly ParticipacaoResumo[]
  assinaturaContrato: TPropostaSchemaInput['detalhesDaProposta']['assinaturaContrato']
  termosDaProposta: string
  observacoes?: string
  onEdit: (step: number) => void
}

const STEP_DETALHES_PROPOSTA = 2

export function DetalhesPropostaSection({
  resumoParticipacao,
  assinaturaContrato,
  termosDaProposta,
  observacoes,
  onEdit,
}: Readonly<TDetalhesPropostaSectionProps>) {
  const { t } = useTranslation('proposta')
  const modeloAssinatura = assinaturaContrato === 'Eletronica' ? 'Eletrônica' : 'Impressa'

  return (
    <div>
      <SectionHeader>
        <SectionTitle>{t('resumo_proposta.detalhes_proposta.titulo')}</SectionTitle>
        <div className="hidden sm:block">
          <Button
            variant="ghost"
            className="h-8 text-sky-700 hover:bg-neutral-200 hover:text-sky-900"
            onClick={() => onEdit(STEP_DETALHES_PROPOSTA)}
          >
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionHeader>
      <SectionContent>
        <Render.If condition={!!resumoParticipacao.length}>
          {() => (
            <div className="space-y-3">
              <h3 className="font-poppins text-base font-normal uppercase">
                {t('resumo_proposta.detalhes_proposta.label.participacao_imobiliaria')}
              </h3>
              {resumoParticipacao.map((imobiliaria) => (
                <React.Fragment key={imobiliaria.id}>
                  <SectionGroup withBorder>
                    <SectionItem label={t('resumo_proposta.detalhes_proposta.label.imobiliaria')}>
                      <SectionItemValue>{imobiliaria.nome}</SectionItemValue>
                    </SectionItem>
                  </SectionGroup>
                  <SectionGroup withBorder className="border-neutral-300">
                    <SectionItem label={imobiliaria.tipo} colSpan={4} withRightBorder>
                      <SectionItemValue>{imobiliaria.documento}</SectionItemValue>
                    </SectionItem>
                    <SectionItem
                      label={t('resumo_proposta.detalhes_proposta.label.participacao')}
                      colSpan={4}
                      withRightBorder
                    >
                      <SectionItemValue>{imobiliaria.participacao}</SectionItemValue>
                    </SectionItem>
                    <SectionItem label={t('resumo_proposta.detalhes_proposta.label.paga_por')} colSpan={4}>
                      <SectionItemValue>{imobiliaria.pagaPor}</SectionItemValue>
                    </SectionItem>
                  </SectionGroup>
                </React.Fragment>
              ))}
            </div>
          )}
        </Render.If>

        <div className="space-y-3">
          <h3 className="font-poppins text-base font-normal uppercase">
            {t('resumo_proposta.detalhes_proposta.label.assinatura')}
          </h3>
          <SectionGroup withBorder className="border-neutral-300">
            <SectionItem label={t('resumo_proposta.detalhes_proposta.label.modelo_assinatura')}>
              <SectionItemValue>{modeloAssinatura}</SectionItemValue>
            </SectionItem>
          </SectionGroup>
        </div>

        <div className="space-y-3">
          <h3 className="font-poppins text-base font-normal uppercase">
            {t('resumo_proposta.detalhes_proposta.label.termos_da_proposta')}
          </h3>
          <TermosDaProposta className="text-sm text-neutral-800" termos={termosDaProposta} assinaturaContrato="-" />
        </div>

        <Render.If condition={!!observacoes}>
          {() => (
            <div className="space-y-3 border-t border-neutral-300 pt-3">
              <h3 className="text-base font-normal uppercase">
                {t('resumo_proposta.detalhes_proposta.label.observacoes')}
              </h3>
              <ul className="ml-2 list-disc space-y-4 pl-5">
                <li className="text-sm text-neutral-800">{observacoes}</li>
              </ul>
            </div>
          )}
        </Render.If>

        <div className="block sm:hidden">
          <Button variant="outline" className="w-full" onClick={() => onEdit(STEP_DETALHES_PROPOSTA)}>
            <Icon name="pencil" className="mr-1 size-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </SectionContent>
    </div>
  )
}
